import { useEffect, useState } from "react";
import { apiFetch } from "../api/api";
import { logClientError } from "../api/errorLogger";

const TICKET_TYPES = [
  { value: "BUG", label: "Bug" },
  { value: "ENHANCEMENT", label: "Enhancement" },
  { value: "UPDATE_REQUEST", label: "Update Request" },
  { value: "OTHER", label: "Other" },
];

const formatLabel = (value) =>
  value
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const formatDate = (date) =>
  new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(date));

export default function Tickets() {
  const [tickets, setTickets] = useState([]);
  const [form, setForm] = useState({
    type: "BUG",
    title: "",
    details: "",
  });
  const [error, setError] = useState("");
  const [listError, setListError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchTickets = async () => {
    setListError("");

    try {
      const data = await apiFetch("/tickets");
      setTickets(data);
    } catch (err) {
      setListError(err.message);
      logClientError(err, { page: "Tickets", action: "fetchTickets" });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsSubmitting(true);

    try {
      await apiFetch("/tickets", {
        method: "POST",
        body: JSON.stringify(form),
      });
      setForm({ type: "BUG", title: "", details: "" });
      await fetchTickets();
    } catch (err) {
      setError(err.message);
      logClientError(err, { page: "Tickets", action: "createTicket" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div>
          <p className="admin-page-eyebrow">Planning & feedback</p>
          <h2>Tickets</h2>
        </div>
      </div>

      <div className="admin-form-panel">
        <form className="admin-form" onSubmit={handleSubmit}>
          <label>
            Type
            <select
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
            >
              {TICKET_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </label>

          <label>
            Title
            <input
              placeholder="Short summary"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              required
            />
          </label>

          <label className="admin-field-wide">
            Details
            <textarea
              placeholder="Describe the request, bug, or improvement"
              value={form.details}
              onChange={(e) => setForm({ ...form, details: e.target.value })}
              required
            />
          </label>

          <div className="admin-form-actions">
            <button className="admin-button admin-button-primary" type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Creating..." : "Create Ticket"}
            </button>
            {error && <p className="admin-form-error">{error}</p>}
          </div>
        </form>
      </div>

      {listError && <p className="admin-empty-state admin-error-state">{listError}</p>}

      {isLoading ? (
        <p className="admin-empty-state">Loading tickets...</p>
      ) : !tickets.length && !listError ? (
        <p className="admin-empty-state">No tickets found.</p>
      ) : tickets.length ? (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Title</th>
                <th>Details</th>
                <th>Status</th>
                <th>Created</th>
                <th>Creator</th>
              </tr>
            </thead>
            <tbody>
              {tickets.map((ticket) => (
                <tr key={ticket.id}>
                  <td>{formatLabel(ticket.type)}</td>
                  <td>{ticket.title}</td>
                  <td className="admin-ticket-details">{ticket.details}</td>
                  <td>
                    <span className="admin-status admin-status-success">
                      {formatLabel(ticket.status)}
                    </span>
                  </td>
                  <td>{formatDate(ticket.createdAt)}</td>
                  <td>{ticket.createdByAdmin?.name || ticket.createdByAdmin?.email || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
